import * as v from 'valibot';
import { conferenceListener } from '../event-listeners/conference-listener';
import JitsiMeetJS from '../lib-jitsi-meet';
import {
  useConfigStore,
  useConnectionStore,
  waitForConnection,
} from '../store';
import { useConferenceStore } from '../store/conference';
import { useParticipantStore } from '../store/participants';
import { getLocalTrack } from '../store/tracks';
import {
  CONFERENCE_COMMANDS,
  MEDIA_TYPE,
  safeParseJson,
  tryCatch,
} from '../utils';
import { UserInfoCommandSchema } from '../utils/schema/user-info-command';

function addConferenceListeners(conference: any) {
  conference.on(
    JitsiMeetJS.events.conference.USER_JOINED,
    conferenceListener.onUserJoined,
  );

  conference.on(
    JitsiMeetJS.events.conference.USER_LEFT,
    conferenceListener.onUserLeft,
  );

  conference.on(
    JitsiMeetJS.events.conference.TRACK_ADDED,
    conferenceListener.onTrackAdded,
  );

  conference.on(
    JitsiMeetJS.events.conference.TRACK_REMOVED,
    conferenceListener.onTrackRemoved,
  );

  conference.on(
    JitsiMeetJS.events.conference.CONFERENCE_JOIN_IN_PROGRESS,
    conferenceListener.onConferenceJoinInProgress,
  );

  conference.on(
    JitsiMeetJS.events.conference.CONFERENCE_FAILED,
    conferenceListener.onConferenceFailed,
  );

  conference.on(
    JitsiMeetJS.events.conference.RECORDER_STATE_CHANGED,
    conferenceListener.onRecorderStateChanged,
  );

  conference.on(
    JitsiMeetJS.events.conference.CONNECTION_ESTABLISHED,
    conferenceListener.onConnectionEstablished,
  );

  conference.on(
    JitsiMeetJS.events.conference.CONNECTION_INTERRUPTED,
    conferenceListener.onConnectionInterrupted,
  );

  conference.on(
    JitsiMeetJS.events.conference.CONNECTION_RESTORED,
    conferenceListener.onConnectionRestored,
  );

  conference.on(
    JitsiMeetJS.events.conference.P2P_STATUS,
    conferenceListener.onP2PStatus,
  );

  conference.on(
    JitsiMeetJS.events.conference.TRACK_MUTE_CHANGED,
    conferenceListener.onTrackMuteChanged,
  );

  conference.on(
    JitsiMeetJS.events.conference.CONFERENCE_JOINED,
    conferenceListener.onConferenceJoined,
  );

  conference.on(
    JitsiMeetJS.events.conference.CONFERENCE_LEFT,
    conferenceListener.onConferenceLeft,
  );

  conference.on(
    JitsiMeetJS.events.conference.PARTICIPANT_SOURCE_UPDATED,
    conferenceListener.onParticipantSourceUpdated,
  );

  conference.on(
    JitsiMeetJS.events.conference.CONFERENCE_CREATED_TIMESTAMP,
    conferenceListener.onConferenceCreatedTimestamp,
  );

  conference.on(
    JitsiMeetJS.events.conference.DOMINANT_SPEAKER_CHANGED,
    conferenceListener.onDominantSpeakerChanged,
  );

  conference.on(
    JitsiMeetJS.events.connectionQuality.LOCAL_STATS_UPDATED,
    conferenceListener.onLocalStatsUpdated,
  );

  conference.on(
    JitsiMeetJS.events.connectionQuality.REMOTE_STATS_UPDATED,
    conferenceListener.onRemoteStatsUpdated,
  );

  conference.on(
    JitsiMeetJS.events.conference.PARTICIPANT_PROPERTY_CHANGED,
    conferenceListener.onParticipantPropertyChanged,
  );

  conference.on(
    JitsiMeetJS.events.conference.USER_ROLE_CHANGED,
    conferenceListener.onUserRoleChanged,
  );

  conference.on(
    JitsiMeetJS.events.conference.TALK_WHILE_MUTED,
    conferenceListener.onTalkWhileMuted,
  );

  conference.on(
    JitsiMeetJS.events.conference.TRACK_AUDIO_LEVEL_CHANGED,
    conferenceListener.onTrackAudioLevelChanged,
  );

  conference.addCommandListener(
    CONFERENCE_COMMANDS.userInfo,
    (data: { value: string }, id: string) => {
      const vData = v.safeParse(
        UserInfoCommandSchema,
        safeParseJson(data.value),
      );
      if (vData.success) {
        useParticipantStore.getState().updateParticipant(id, {
          avatar: vData.output.avatar,
          uid: vData.output.uid,
        });
      }
    },
  );
}

export async function _createConference() {
  const sessionId = useConfigStore.getState().sessionId;
  const connection = useConnectionStore.getState().connection;
  if (!connection) {
    throw new Error('No connection available');
  }
  const connectionStatus = useConnectionStore.getState().connectionStatus;
  if (connectionStatus !== 'connected') {
    await waitForConnection();
  }
  const existingConference = useConferenceStore.getState().conference;
  if (existingConference) {
    console.log('Conference already exists, skipping creation');
    return;
  }
  const connectionConfig = useConnectionStore.getState().connectionConfig;
  const conference = connection.initJitsiConference(
    sessionId,
    connectionConfig,
  );

  const localAudioTrack = getLocalTrack(MEDIA_TYPE.AUDIO)?.originalTrack;
  const localVideoTrack = getLocalTrack(MEDIA_TYPE.VIDEO)?.originalTrack;

  if (localAudioTrack) {
    conference.addTrack(localAudioTrack);
  }
  if (localVideoTrack) {
    conference.addTrack(localVideoTrack);
  }

  useParticipantStore.getState().updateLocalParticipant({
    pid: conference.myUserId(),
  });
  addConferenceListeners(conference);
  useConferenceStore.getState().conferenceWillJoin(conference);
  conference.setDisplayName(
    useParticipantStore.getState().localParticipant.name,
  );
  conference.join();
}

export async function createConference() {
  const conference = useConferenceStore.getState().conference;
  if (!conference) {
    const result = await tryCatch(_createConference());
    if (result.error) {
      console.error('Error creating conference', result.error);
      useConferenceStore.setState({
        conferenceStatus: 'error',
        conferenceJoined: false,
        conferenceError: result.error.message,
      });
    }
  }
}

export function muteParticipant(participantId: string) {
  const conference = useConferenceStore.getState().conference;
  conference?.muteParticipant(participantId, 'audio');
}

export function pauseParticipantVideo(participantId: string) {
  const conference = useConferenceStore.getState().conference;
  conference?.muteParticipant(participantId, 'video');
}
